/**
 * Hook to fetch custom field definitions for deals from CRM ingestion metadata.
 *
 * This is the proper way to get custom field definitions — query the field metadata
 * from the backend rather than iterating through deal records.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  useGetCrmIngestionFieldsLazyQuery,
  CrmIngestionPlatform,
  CrmIngestionObjectType,
} from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import {
  columnTypeFromCustomFieldType,
  aiPromptFromFieldConfig,
  formulaFromFieldConfig,
  formulaResultTypeFromFieldConfig,
} from '@/lib/utils/custom-column';
import { standardFieldsFromCrmIngestion } from '@/lib/utils/crm-custom-fields';
import { usePreviewCustomFieldsFor, mergeCustomFieldLists } from '@/lib/hooks/use-preview-custom-fields';
import { useMethodologyExcludedColumnKeys } from '@/lib/hooks/use-methodology-excluded-keys';

export interface CustomFieldDefinition {
  id: string; // Backend field ID
  key: string; // e.g., "custom_field_name"
  label: string; // Display name
  type: string; // "text", "number", "date", etc.
  crmName: string; // Backend field name
  selectOptions?: string[]; // For single-select and multi-select types
  aiPrompt?: string; // AI_SEARCH fields: research prompt, parsed from fieldConfig
  formula?: string; // FORMULA fields: expression, parsed from fieldConfig
  formulaResultType?: string; // FORMULA fields: "number" | "string" | "date"
  fieldConfig?: string | null; // Raw backend fieldConfig, so edits can merge into it
}

// Stable reference for the pessimistic pre-scope window, so consumers'
// downstream memos don't churn on every render.
const EMPTY_FIELDS: CustomFieldDefinition[] = [];

export interface UseDealCustomFieldsOptions {
  /**
   * Hide fields belonging to sales methodologies the workspace didn't pick
   * (see use-methodology-excluded-keys.ts). Opt-in per surface: grid/detail
   * display surfaces pass true; config surfaces (Data Model, rules, agents,
   * entry criteria) must stay unscoped so existing references to hidden
   * fields remain visible and manageable.
   */
  methodologyScope?: boolean;
}

export const useDealCustomFields = (options?: UseDealCustomFieldsOptions) => {
  const scopeEnabled = options?.methodologyScope === true;
  const scope = useMethodologyExcludedColumnKeys(scopeEnabled);
  const workspaceId = useWorkspaceId();
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);
  const previewFields = usePreviewCustomFieldsFor('Deal');
  // True once the definitions query has settled (data or error), i.e. `customFields`
  // reflects the backend rather than the initial []. Set in the same effect that
  // sets customFields so consumers never observe hydrated=true with stale fields.
  // Lets consumers (grid summaries) wait for the final column set instead of
  // firing once for standard columns and again when custom columns arrive.
  const [hydrated, setHydrated] = useState(false);

  const [getCrmIngestionFields, { data, loading, error }] =
    useGetCrmIngestionFieldsLazyQuery({
      // cache-first: see use-contact-custom-fields.ts — mutations that change
      // field definitions refresh this operation explicitly, so consumers can
      // share the cached result instead of duplicating the network call.
      fetchPolicy: 'cache-first',
    });

  useEffect(() => {
    if (workspaceId) {
      getCrmIngestionFields({
        variables: {
          workspaceId: parseInt(workspaceId),
          platform: CrmIngestionPlatform.Coffee, // ⚠️ Use COFFEE (not Salesforce/HubSpot)
          first: 100,
        },
      });
    }
  }, [workspaceId, getCrmIngestionFields]);

  useEffect(() => {
    if (data?.crmIngestionFields?.edges) {
      const fields = data.crmIngestionFields.edges
        .map(edge => edge?.node)
        .filter((field): field is NonNullable<typeof field> => {
          return (
            field != null &&
            field.objectType === CrmIngestionObjectType.Deal && // ⚠️ DEAL not CONTACT or ACCOUNT
            field.availableInGrid === true &&
            !field.isStandardField // Only custom fields
          );
        })
        .map(field => {
          const customFieldType = field.customFieldType?.toLowerCase() || 'text';

          // Parse enumerationData for select options (single-select and multi-select)
          let selectOptions: string[] | undefined;
          if (field.isEnumeration && field.enumerationData && (customFieldType === 'single_select' || customFieldType === 'multi_select')) {
            try {
              const parsed = JSON.parse(field.enumerationData as string);
              selectOptions = (parsed?.options || []).map((opt: any) => opt.label || opt.value);
            } catch (e) {
              console.error('Failed to parse enumerationData for', field.crmName, e);
            }
          }

          return {
            id: field.id,
            key: `custom_${field.crmName}`,
            label: field.crmLabel,
            type: columnTypeFromCustomFieldType(field.customFieldType), // Map backend enum → valid UI ColumnType
            crmName: field.crmName,
            selectOptions,
            aiPrompt: aiPromptFromFieldConfig(field.fieldConfig),
            formula: formulaFromFieldConfig(field.fieldConfig),
            formulaResultType: formulaResultTypeFromFieldConfig(field.fieldConfig),
            fieldConfig: field.fieldConfig,
          };
        });

      setCustomFields(fields);
    }
    // Error also hydrates: consumers should proceed with standard columns rather
    // than wait forever for definitions that will never arrive.
    if (data || error) setHydrated(true);
  }, [data, error]);

  // Memoize the merge so consumers get a stable array reference across renders
  // (fresh array every render would defeat downstream memoization; this hook
  // feeds the Deals grid and the Deal detail sheet).
  const mergedCustomFields = useMemo(
    () => mergeCustomFieldLists(customFields, previewFields as CustomFieldDefinition[]),
    [customFields, previewFields],
  );

  // Dev-only drift signal: scoping hinges entirely on the backend crm_name
  // convention `<fieldName>_<wsId>_coffee` (see use-methodology-excluded-keys).
  // If we computed a non-empty exclusion set but none of it matches an actual
  // backend column, the convention or seeding likely changed and scoping has
  // become a silent no-op — surface that instead of hiding it. (Zero matches
  // can also be legitimate — e.g. no meetings have materialized the ingestion
  // rows yet — hence a warning, and only in dev.)
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!scopeEnabled || !scope.resolved || scope.excludedKeys.size === 0) return;
    if (customFields.length === 0) return;
    if (!customFields.some(f => scope.excludedKeys.has(f.key))) {
      console.warn(
        '[methodology-scope] exclusion set matched no deal custom-field columns — crm_name convention drift? Scoping is a no-op.',
        [...scope.excludedKeys],
      );
    }
  }, [scopeEnabled, scope.resolved, scope.excludedKeys, customFields]);

  // Methodology scoping (opt-in). Pessimistic while the scope queries are in
  // flight: return no custom fields rather than flash every methodology's
  // columns and yank the excluded ones — indistinguishable from the existing
  // pre-hydration window where customFields is still []. Scope failures fail
  // open (empty exclusion set with resolved=true), so this can never hold the
  // grid back permanently.
  const scopedFields = useMemo(() => {
    if (!scopeEnabled) return mergedCustomFields;
    if (!scope.resolved) return EMPTY_FIELDS;
    if (scope.excludedKeys.size === 0) return mergedCustomFields;
    return mergedCustomFields.filter(f => !scope.excludedKeys.has(f.key));
  }, [scopeEnabled, scope.resolved, scope.excludedKeys, mergedCustomFields]);

  // Standard (non-custom) field definitions for this object. Formula fields
  // reference them by `crmName`, and the backend validates references against
  // the very same rows, so this list is the safe vocabulary to offer.
  const standardFields = useMemo(
    () => standardFieldsFromCrmIngestion(data, CrmIngestionObjectType.Deal),
    [data],
  );

  return {
    customFields: scopedFields,
    standardFields,
    loading,
    error,
    // Scoped consumers also wait for the exclusion set, preserving the
    // invariant that hydrated=true means the final column set.
    hydrated: hydrated && (!scopeEnabled || scope.resolved),
  };
};
